import React, { useEffect, useRef, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { commafy } from 'Utils';
import EntityMarkSeries from '../EntityPanel/EntityMarkSeries';
import {
  getResultsTablesPage,
  buildEntityTablesFromOverview,
  buildTableSampledRowCountsMapForEntityTables,
} from 'DataDiscovery/discoverApi';

// Single fixed page of the biggest tables by findings — this is the whole point of
// table-index mode: never load all N tables client-side. detail=full items are the
// exact OverviewTableItem shape (fields + findings), so they feed EntityMarkSeries
// through the same builders the classic `tables` array uses.
const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Scatter for table-index (PeopleSoft-80K) runs. Renders the same
 * "Sensitive Data Distribution" scatter as classic runs, but instead of loading every
 * table client-side it fetches exactly one page — the top PAGE_SIZE tables by finding
 * count — from `results/tables`. Search (the scatter's own search box) and the entity
 * drill-down route both refetch server-side (`q=`, `entity=`) instead of filtering the
 * already-loaded page, since a match outside the top PAGE_SIZE would otherwise be
 * invisible.
 */
const IndexResultsPanel = (props) => {
  const searchid = props.searchid;
  const dataLabel = props.dataLabel || 'tables';
  const entity = props.entity || 'ALL';
  const totalTables = Number(props.totalTables) || 0;
  const partialTick = props.partialTick;

  const [state, setState] = useState({ loading: true, error: null, tables: [] });
  const [activeQuery, setActiveQuery] = useState('');

  const requestSeq = useRef(0);

  const load = async (q) => {
    const seq = (requestSeq.current += 1);
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = { sort: 'findings', dir: 'desc', size: PAGE_SIZE, detail: 'full' };
      if (q) params.q = q;
      if (entity && entity !== 'ALL') params.entity = entity;
      const page = await getResultsTablesPage(searchid, params);
      if (seq !== requestSeq.current) return; // stale response — a newer request superseded it
      setState({ loading: false, error: null, tables: (page && page.tables) || [] });
    } catch (e) {
      if (seq !== requestSeq.current) return;
      console.log('index results page', e);
      setState({ loading: false, error: e?.message || 'Failed to load results', tables: [] });
    }
  };

  useEffect(() => {
    if (searchid) load(activeQuery);
    // Re-fetch on searchid/entity change (route nav) and on a fresh partial-results
    // snapshot; activeQuery changes are handled by the debounced search handler below,
    // which calls load() itself, so it's intentionally excluded here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchid, entity, partialTick]);

  // The scatter's own search box (data-testid="search-overview-scatter") calls this via
  // EntityMarkSeries' onServerSearch prop instead of filtering the loaded page locally.
  const debounceRef = useRef(null);
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);
  const handleServerSearch = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setActiveQuery(value);
      load(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const tables = state.tables;
  const entityTables = buildEntityTablesFromOverview(tables);
  const tablesToSampledRows = buildTableSampledRowCountsMapForEntityTables(tables);

  const subHeaderNote = !activeQuery && totalTables > PAGE_SIZE
    ? `Showing top ${PAGE_SIZE} of ${commafy(totalTables)} ${dataLabel} by findings. Use search to find a specific ${dataLabel.replace(/s$/, '')}.`
    : null;

  if (state.loading && tables.length === 0) {
    return (
      <Card className='shadow-sm widget-card text-left'>
        <div className='d-flex justify-content-center' style={{ padding: 32 }}>
          <Spinner variant='primary' animation='border' />
        </div>
      </Card>
    );
  }

  if (state.error) {
    return (
      <Card className='shadow-sm widget-card text-left'>
        <div className='text-center' style={{ padding: 24, fontSize: 14, color: '#B91C1C' }}>
          {state.error}
        </div>
      </Card>
    );
  }

  return (
    <EntityMarkSeries
      searchid={searchid}
      dataLabel={dataLabel}
      lowConfidence={false}
      overviewTables={tables}
      entityTables={entityTables}
      entity={entity}
      tablesToSampledRows={tablesToSampledRows}
      onServerSearch={handleServerSearch}
      subHeaderNote={subHeaderNote}
    />
  );
};

export default IndexResultsPanel;
